import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Tooltip, Space, Modal, message, Select, Tag } from 'antd';
import { EditOutlined, DeleteOutlined} from '@ant-design/icons';
import { AppDispatch, RootState } from '../../redux/store/store';
import { fetchPacientesEmpresa, deletePaciente, setPacientesPage, setPacientesLimit } from '../../redux/actions/pacientes/Pacientes';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

interface ColumnItem {
  key: string;
  title: string;
  dataIndex: string;
  width?: string | number;
  ellipsis?: boolean;
  render?: any;
}

const ListaPacientes: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pacientes, loading, pagination } = useSelector((state: RootState) => state.pacientes);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPacientesEmpresa(pagination.page, pagination.limit));
  }, [pagination.page, pagination.limit, searchTerm]);

  const handleTableChange = (pagination: any) => {
    dispatch(setPacientesPage(pagination.current));
    dispatch(fetchPacientesEmpresa(pagination.current, pagination.pageSize));
  };

  const handlePageSizeChange = (current: number, size: number) => {
    dispatch(setPacientesLimit(size));
    dispatch(fetchPacientesEmpresa(current, size));
  };

  const handleEditPaciente = (id: number) => {
    navigate(`/pacientes/editar/${id}`);
  };

  const showDeleteConfirm = (id: number) => {
    setSelectedPatientId(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedPatientId) {
      setDeleteLoading(true);
      try {
        await dispatch(deletePaciente(selectedPatientId.toString()));
        setDeleteModalVisible(false);
        dispatch(fetchPacientesEmpresa(pagination.page, pagination.limit));
        message.success('Paciente eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar el paciente:', error);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setSelectedPatientId(null);
  };

  const columns: ColumnItem[] = [
    {
      key: 'item',
      title: 'Item',
      dataIndex: 'id',
      width: '5%',
      ellipsis: true,
      render: (_: any, __: any, index: number) => (pagination.page - 1) * pagination.limit + index + 1
    },
    {
      key: 'nombre',
      title: 'Paciente',
      dataIndex: 'nombre',
      width: '20%',
      ellipsis: true,
      render: (nombre: string, record: any) => (
        <Tooltip title={`${nombre} ${record.apellidos}`}>
          <div className="flex items-center gap-2">
            <span className="font-medium">{`${nombre} ${record.apellidos}`}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'cedula',
      title: 'Cédula',
      dataIndex: 'cedula',
      width: '10%',
      ellipsis: true,
      render: (cedula: string) => (
        <Tooltip title={cedula}>
          <span>{cedula}</span>
        </Tooltip>
      ),
    },
    {
      key: 'contacto',
      title: 'Contacto',
      dataIndex: 'email',
      width: '15%',
      ellipsis: true,
      render: (email: string, record: any) => (
        <Tooltip title={`Email: ${email}, Celular: ${record.celular}`}>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Email: {email}</span>
            <span className="text-xs text-gray-500">Cel: {record.celular}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'direccion',
      title: 'Dirección',
      dataIndex: 'direccion',
      width: '15%',
      ellipsis: true,
      render: (direccion: string) => (
        <Tooltip title={direccion}>
          <span className="truncate block">{direccion}</span>
        </Tooltip>
      ),
    },
    {
      key: 'empresa',
      title: 'Empresa',
      dataIndex: 'empresa_info',
      width: '15%',
      ellipsis: true,
      render: (empresa_info: any) => {
        const nombreEmpresa = empresa_info?.nombre || 'Sin empresa';
        
        return (
          <Tooltip title={nombreEmpresa}>
            <Tag color="blue">{nombreEmpresa}</Tag>
          </Tooltip>
        );
      },
    },
    {
      key: 'fecha_creacion',
      title: 'Fecha de Creación',
      dataIndex: 'createdAt',
      width: '12%',
      ellipsis: true,
      render: (createdAt: string) => (
        <Tooltip title={createdAt ? new Date(createdAt).toLocaleDateString() : 'Sin fecha'}>
          <span className="text-sm text-gray-600">
            {createdAt ? new Date(createdAt).toLocaleDateString() : 'Sin fecha'}
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'acciones',
      title: 'Acciones',
      dataIndex: 'id',
      width: '10%',
      render: (id: number) => (
        <Space size="middle">
          <Tooltip title="Editar">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditPaciente(id)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Lista de Pacientes</h1>
      </div>

      <Table
        columns={columns}
        dataSource={pacientes}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Total ${total} pacientes`,
          onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          onShowSizeChange: handlePageSizeChange
        }}
      />

      <Modal
        title="Confirmar eliminación"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleCancelDelete}
        confirmLoading={deleteLoading}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro que desea eliminar este paciente? Esta acción no se puede deshacer.</p>
      </Modal>
    </div>
  );
};

export default ListaPacientes;